import { useMemo } from 'react'

import { useAtomValue } from 'jotai'
import { selectAtom } from 'jotai/utils'

import type { Cue } from '~shared/rundown/cue'
import type { IndexedCue, Timestamp } from '~shared/utils'
import { fastDeepEqual } from '~shared/utils'

import { activeCueIdAtom, runnerNextCueIdAtom } from '@/store/runner.store'
import { timestampsAtom } from '@/store/timestamps.store'

import { useCueSnapshot } from '../data/cues.store'

import { cueUiActions } from './cue-ui.store'
import { useCuesDerived } from './cue.derived'

import type { Snapshot } from 'valtio'

/**
 * Cue lookups that depend on the component that asks, or on state outside the
 * cue stores (the runner). Anything that holds for the whole rundown belongs in
 * `cue.derived.ts` instead, where it is computed once.
 */

/**
 * Subscribe to one cue document.
 *
 * Accepts a nullish id so callers can look up an optional relation (a row's
 * parent group, say) without branching around the hook.
 *
 * @returns undefined while the cue's document has not arrived, and after it is
 *   deleted. A row is removed by the order changing, not by this going empty.
 */
export function useCue(cueId: string | null | undefined): Cue | undefined {
  return useCueSnapshot(cueId ?? '')
}

/**
 * Position and display index of one cue. See `IndexedCue`.
 *
 * Returns the valtio snapshot, so nested arrays (`children`) come back
 * readonly. That is what a snapshot is; do not widen it with a cast.
 */
export function useIndexedCue(cueId: string): Snapshot<IndexedCue> | undefined {
  const { indexedCuesMap } = useCuesDerived()

  return indexedCuesMap[cueId]
}

/** Rows the editor renders: children of collapsed groups are left out. */
export function useVisibleIndexedCues(): readonly Snapshot<IndexedCue>[] {
  return useCuesDerived().visibleIndexedCues
}

/** Closest playable cue id before `cueId`, skipping headings and groups. */
export function usePreviousCueId(cueId: string): string | null {
  const { playableNeighborsMap } = useCuesDerived()

  return playableNeighborsMap[cueId]?.prevId ?? null
}

/** Closest playable cue id after `cueId`. See {@link usePreviousCueId}. */
export function useNextCueId(cueId: string): string | null {
  const { playableNeighborsMap } = useCuesDerived()

  return playableNeighborsMap[cueId]?.nextId ?? null
}

/**
 * Closest playable cue after `cueId`, skipping headings and groups.
 *
 * The neighbor id comes from the derived neighbors map, so only this row's
 * entry and the neighbor's own document are subscribed to. A change to any
 * other cue does not re-render the caller.
 */
export function useNextCue(cueId: string): Cue | null {
  return useCue(useNextCueId(cueId)) ?? null
}

/**
 * Subscribe to one cue's timestamp.
 *
 * Selects the cue's own entry out of the timestamps object, so a recompute
 * re-renders only the rows whose timestamp actually changed. `fastDeepEqual`
 * is required because `createTimestamps` builds fresh objects on every run.
 */
export function useCueTimestamp(
  cueId: string | null | undefined,
): Timestamp | null {
  const timestampAtom = useMemo(
    () =>
      selectAtom(
        timestampsAtom,
        (timestamps) => (cueId ? (timestamps?.cues[cueId] ?? null) : null),
        fastDeepEqual,
      ),
    [cueId],
  )

  return useAtomValue(timestampAtom)
}

/**
 * Is this cue on air?
 *
 * Memoized so it only re-renders when the answer flips for THIS cue.
 */
export function useIsActiveCue(cueId: string): boolean {
  const isActiveAtom = useMemo(
    () => selectAtom(activeCueIdAtom, (id) => id === cueId),
    [cueId],
  )

  return useAtomValue(isActiveAtom)
}

/**
 * Has the runner queued this cue as next?
 *
 * Same contract as {@link useIsActiveCue}.
 */
export function useIsRunnerNextCue(cueId: string): boolean {
  const isNextAtom = useMemo(
    () => selectAtom(runnerNextCueIdAtom, (id) => id === cueId),
    [cueId],
  )

  return useAtomValue(isNextAtom)
}

/** Is the running cue the first playable cue of the rundown? */
export function useIsFirstCue(): boolean {
  const { firstCueId } = useCuesDerived()
  const activeCueId = useAtomValue(activeCueIdAtom)

  return Boolean(firstCueId) && firstCueId === activeCueId
}

/**
 * Does this row lead into a soft-scheduled cue? See `buildSoftSchedulePath`.
 *
 * Headings and groups answer for the next playable cue below them.
 */
export function useIsCueInSoftSchedulePath(cueId: string | null): boolean {
  const { softSchedulePathCueIds } = useCuesDerived()

  return cueId !== null && softSchedulePathCueIds[cueId] === true
}

/**
 * Collapse every group in the rundown, or expand all of them.
 *
 * The group ids come from the derived list, which the store deliberately does
 * not know about, so the two are tied together here.
 */
export function useToggleAllGroups(): (collapsed: boolean) => void {
  const { groupCueIds } = useCuesDerived()

  return useMemo(
    () => (collapsed: boolean) =>
      cueUiActions.toggleAllGroups([...groupCueIds], collapsed),
    [groupCueIds],
  )
}
